// frontend/modules/in-app-notifications/components/NotificationItem.tsx
import React from 'react';
import { Card, Typography, Tag, Dropdown, Button, Tooltip, Avatar } from 'antd';
import {
    MoreOutlined,
    EyeOutlined,
    CheckOutlined,
    DeleteOutlined,
    LinkOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    ExclamationCircleOutlined,
    AlertOutlined,
    ArrowRightOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import { InAppNotification, NotificationPriority, ActionButton } from '../types';
import {useTheme} from "@/components/common/ThemeProvider";
import dayjs from 'dayjs';
import { RenderedNotificationContent as OrvalRenderedContent } from "@/shared/api/model";

const { Text, Paragraph } = Typography;

interface NotificationItemProps {
    notification: InAppNotification;
    size?: 'small' | 'default';
    onMarkAsRead?: (id: number) => void;
    onMarkAsAttended?: (id: number) => void;
    onDelete?: (id: number) => void;
    onClick?: (notification: InAppNotification) => void;
}

// Local interface that matches our needs
interface LocalRenderedContent {
    title: string;
    message: string;
    priority: NotificationPriority;
    category: string;
    actionLink?: string;
    primaryAction?: ActionButton;
    secondaryAction?: ActionButton;
    thumbnail?: string;
}

const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
        case 'CRITICAL': return '#ff4d4f';
        case 'ALERT': return '#fa8c16';
        case 'WARNING': return '#fadb14';
        case 'INFORMATIONAL': 
        default: return '#1890ff';
    }
};

const getPriorityIcon = (priority: NotificationPriority) => {
    switch (priority) {
        case 'CRITICAL': return <ExclamationCircleOutlined />;
        case 'ALERT': return <AlertOutlined />;
        case 'WARNING': return <WarningOutlined />;
        case 'INFORMATIONAL':
        default: return <InfoCircleOutlined />;
    }
};

const ActionButtonComponent: React.FC<{
    action: ActionButton;
    size?: 'small' | 'middle' | 'large';
}> = ({ action, size = 'small' }) => {
    const handleClick = () => {
        if (action.url) {
            if (action.external) {
                window.open(action.url, '_blank');
            } else {
                window.location.href = action.url;
            }
        }
    };

    const buttonType = action.type === 'PRIMARY' ? 'primary' : 
                      action.type === 'DANGER' ? 'default' : 'default';
    const danger = action.type === 'DANGER';

    return (
        <Button
            type={buttonType}
            size={size}
            danger={danger}
            icon={action.external ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
            onClick={handleClick}
        >
            {action.label || 'Action'}
        </Button>
    );
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
    notification,
    size = 'default',
    onMarkAsRead,
    onMarkAsAttended,
    onDelete,
    onClick
}) => {
    const { dark } = useTheme();

    // Parse rendered content safely
    const content: LocalRenderedContent = React.useMemo(() => {
        if (notification.renderedContent) {
            // Convert Orval type to local type with safe defaults
            const orvalContent = notification.renderedContent as OrvalRenderedContent;
            return {
                title: orvalContent.title || notification.title || '',
                message: orvalContent.message || notification.message || '',
                priority: (orvalContent.priority as NotificationPriority) || (notification.priority as NotificationPriority) || 'INFORMATIONAL',
                category: orvalContent.category || notification.category || 'GENERAL',
                actionLink: orvalContent.actionLink || notification.actionLink,
                primaryAction: orvalContent.primaryAction,
                secondaryAction: orvalContent.secondaryAction,
                thumbnail: orvalContent.thumbnail
            };
        }
        
        // Fallback to basic content structure
        return {
            title: notification.title || '',
            message: notification.message || '',
            priority: (notification.priority as NotificationPriority) || 'INFORMATIONAL',
            category: notification.category || 'GENERAL',
            actionLink: notification.actionLink
        };
    }, [notification]);

    const menuItems = [
        {
            key: 'read',
            label: notification.read ? 'Mark as Unread' : 'Mark as Read',
            icon: <EyeOutlined />,
            disabled: notification.read,
            onClick: () => notification.id && onMarkAsRead?.(notification.id)
        },
        {
            key: 'attended',
            label: notification.attended ? 'Mark as Unattended' : 'Mark as Attended',
            icon: <CheckOutlined />,
            disabled: notification.attended,
            onClick: () => notification.id && onMarkAsAttended?.(notification.id)
        },
        ...(content.actionLink || content.primaryAction ? [{
            key: 'action',
            label: 'Go to Action',
            icon: <LinkOutlined />,
            onClick: () => {
                const link = content.actionLink || content.primaryAction?.url;
                if (link) {
                    if (content.primaryAction?.external) {
                        window.open(link, '_blank');
                    } else {
                        window.location.href = link;
                    }
                }
            }
        }] : []),
        {
            type: 'divider' as const
        },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => notification.id && onDelete?.(notification.id)
        }
    ];

    const isAttended = notification.attended;

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        return dayjs(dateString).format('MMM DD, YYYY HH:mm');
    };

    const createdAtFormatted = formatDate(notification.createdAt);
    const readAtFormatted = formatDate(notification.readAt);

    return (
        <Card
            size={size}
            className={`notification-item ${!notification.read ? 'unread' : ''}`}
            style={{
                marginBottom: 8,
                borderLeft: `4px solid ${getPriorityColor(content.priority)}`,
                backgroundColor: !notification.read 
                    ? (dark ? '#1f1f1f' : '#fafafa')
                    : 'transparent',
                cursor: onClick ? 'pointer' : 'default'
            }}
            onClick={() => onClick?.(notification)}
            extra={
                <Dropdown
                    menu={{ items: menuItems }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        icon={<MoreOutlined />}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                    />
                </Dropdown>
            }
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Thumbnail or Icon */}
                    {content.thumbnail ? (
                        <Avatar src={content.thumbnail} size="small" />
                    ) : (
                        <div style={{ 
                            fontSize: '16px', 
                            color: getPriorityColor(content.priority) 
                        }}>
                            {getPriorityIcon(content.priority)}
                        </div>
                    )}
                    
                    <Text strong style={{ fontSize: '14px', flex: 1 }}>
                        {content.title}
                    </Text>
                    
                    {!notification.read && (
                        <div
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#1890ff',
                                flexShrink: 0
                            }}
                        />
                    )}
                </div>
            </div>
            
            <div style={{ marginTop: 8 }}>
                <Paragraph
                    style={{ 
                        fontSize: '13px', 
                        color: dark ? '#d9d9d9' : '#666',
                        marginBottom: 12,
                        lineHeight: '1.4'
                    }}
                    ellipsis={{ 
                        rows: 2, 
                        tooltip: content.message 
                    }}
                >
                    {content.message}
                </Paragraph>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Tags */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Tag 
                            color={getPriorityColor(content.priority)} 
                            style={{ fontSize: '11px', padding: '0 6px' }}
                        >
                            {content.priority}
                        </Tag>
                        
                        {content.category && (
                            <Tag style={{ fontSize: '11px', padding: '0 6px' }}>
                                {content.category}
                            </Tag>
                        )}
                        
                        {notification.expiryDate && new Date(notification.expiryDate) < new Date() && (
                            <Tag color="red" style={{ fontSize: '11px', padding: '0 6px' }}>
                                Expired
                            </Tag>
                        )}
                        
                        {!notification.read && (
                            <Tag color="orange" style={{ fontSize: '11px', padding: '0 6px' }}>
                                Unread
                            </Tag>
                        )}
                        
                        {isAttended && (
                            <Tag color="green" style={{ fontSize: '11px', padding: '0 6px' }}>
                                Attended
                            </Tag>
                        )}
                    </div>
                </div>
                
                {/* Timestamp */}
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                    <Text 
                        type="secondary" 
                        style={{ fontSize: '11px', display: 'block' }}
                    >
                        {createdAtFormatted}
                    </Text>
                    {readAtFormatted && (
                        <Text 
                            type="secondary" 
                            style={{ fontSize: '11px' }}
                        >
                            {readAtFormatted}
                        </Text>
                    )}
                </div>
            </div>
        </Card>
    );
};
